import { HintAnimation } from '../hint-motion';
import { bubble, hline, okChip, pill, text } from '../../academy/films/story-scene';
import { abs, C, glass, icon, orbiTemplate, PRESSED } from './scene-kit';
import { CONTENT, HintStory, navPoint, NavItem, portalShell } from './hint-story';

/**
 * Hinweis G „Sieh dir an, was deine Lernenden fragen“.
 *
 * Didaktik: Sobald echte Gespräche laufen, ist die wertvollste Quelle für
 * Verbesserungen das, was Lernende tatsächlich fragen — und wo die Antwort
 * nicht gepasst hat. WARUM „Was fragen deine Lernenden? Lies mit und
 * verbessere“ → WO Analytics › Konversationen → WAS das Gespräch mit dem
 * Daumen runter öffnen, Frage und Antwort lesen → GESCHAFFT „Daraus lernen“
 * macht aus der Stelle ein Learning für den Agenten, Orbi lacht. Der Film
 * schließt den Kreis: lesen → verstehen → verbessern.
 */

const NAV: NavItem[] = [
  ['agent', 'KI-Agenten'],
  ['brain', 'Wissensbasis'],
  ['chart', 'Analytics'],
];
const SUB = ['Echtzeit', 'Events', 'Sitzungen', 'Semantik', 'Konversationen'];
const subY = (i: number) => 136 + 24 * i;
const SUB_CONV: [number, number] = [85, 56 + subY(4) + 11];
const ROW1: [number, number] = [CONTENT.x + 220, CONTENT.y + 106 + 19];
const LEARN: [number, number] = [CONTENT.x + 330 + 50, CONTENT.y + 204 + 16];
const DOWN = '#C23A10';

const subMenu =
  `<div data-r="sub" style="opacity:0;">` +
  SUB.map(
    (label, i) =>
      `<div style="${abs(48, 56 + subY(i), 106, 22)}">` +
      (i === 4 ? `<div data-r="subSel" style="position:absolute;inset:0;border-radius:11px;opacity:0;${PRESSED}"></div>` : '') +
      `<div style="position:relative;height:100%;display:flex;align-items:center;padding:0 10px;font-size:11.5px;font-weight:500;color:${i === 4 ? C.text : C.muted};white-space:nowrap;">${label}</div></div>`,
  ).join('') +
  `</div>`;

const agentsPage = `<div data-r="pageA">` + text(20, 12, 300, 'KI-Agenten', 18, 400) + hline(16, 46, 428) + `</div>`;

const convRow = (y: number, when: string, q: string, turns: number, down: boolean, ref: string) =>
  `<div data-r="${ref}" style="${abs(16, y, 428, 38)}">` +
  `<div style="position:absolute;inset:0;border-radius:12px;background:rgb(255 255 255 / .42);box-shadow:inset 0 0 0 1px rgb(255 255 255 / .7);"></div>` +
  `<div style="position:relative;height:100%;display:flex;align-items:center;gap:10px;padding:0 12px;font-size:11.5px;color:${C.text};white-space:nowrap;">` +
  icon('chevron', 12, C.muted, 2, 'transform:rotate(-90deg);') +
  `<span style="color:${C.muted};font-variant-numeric:tabular-nums;">${when}</span>` +
  `<span style="color:${C.muted};">Statistik-Tutor</span>` +
  `<span style="flex:1;font-weight:600;overflow:hidden;text-overflow:ellipsis;">${q}</span>` +
  `<span style="font-size:10.5px;font-weight:600;color:${C.muted};">${turns}</span>` +
  (down ? `<span style="display:flex;align-items:center;gap:3px;font-size:10.5px;font-weight:600;color:${DOWN};">${icon('thumbDown', 12, DOWN, 2)}1</span>` : '') +
  `</div></div>`;

const thread =
  `<div data-r="thread" style="${abs(16, 148, 428, 92, 'opacity:0;')}">` +
  bubble(214, 4, 200, 'Wie berechne ich die Varianz?', true) +
  `<div style="${abs(14, 38, 220, 44)}">${bubble(0, 0, 220, 'Die Varianz ist die Wurzel der Standardabweichung.', false)}</div>` +
  `<div style="${abs(240, 52, 20, 20, 'display:flex;align-items:center;justify-content:center;')}">${icon('thumbDown', 14, DOWN, 2)}</div>` +
  `<div data-r="learnBtn" style="${abs(314, 56, 100, 32)}">${pill(0, 0, 100, 'Daraus lernen')}</div>` +
  `<div data-r="learnGlow" style="${abs(314, 56, 100, 32, 'border-radius:999px;')}"></div>` +
  okChip(290, 62, 124, 'Learning angelegt', 'learned') +
  `<div data-r="cvOrbi" style="${abs(262, 58, 26, 26, 'opacity:0;')}">${orbiTemplate('cv', 26)}</div>` +
  `</div>`;

const convPage =
  `<div data-r="pageB" style="opacity:0;">` +
  text(20, 12, 200, 'Konversationen', 18, 400) +
  `<div style="${abs(360, 14, 84, 26, `border-radius:999px;background:${C.action};color:#fff;font-size:11px;font-weight:600;display:flex;align-items:center;justify-content:center;`)}">30 Tage</div>` +
  hline(16, 50, 428) +
  convRow(62, '09:12', 'Was ist ein Median?', 3, false, 'row0') +
  `<div data-r="row1Glow" style="${abs(16, 106, 428, 38, 'border-radius:12px;')}"></div>` +
  convRow(106, '08:47', 'Wie berechne ich die Varianz?', 2, true, 'row1') +
  convRow(150, '08:30', 'Welche Klausurthemen kommen dran?', 5, false, 'row2') +
  thread +
  `</div>`;

export const reviewConversationsAnimation = (): HintAnimation =>
  new HintStory({
    intro: { line1: ['Was', 'fragen', 'deine', 'Lernenden?'], line2: ['Lies', 'mit', 'und', 'verbessere'] },
    portal: portalShell({ nav: NAV, active: 0, content: agentsPage + convPage }) + subMenu,
    tracks: [
      { r: 'sub', at: [1.42, 1.58], o: [0, 1], y: [-6, 0] },
      { r: 'nav0', at: [1.8, 1.9], o: [1, 0] },
      { r: 'subSel', at: [1.8, 1.9], o: [0, 1] },
      { r: 'pageA', at: [1.84, 1.95], o: [1, 0] },
      { r: 'pageB', at: [1.92, 2.1], o: [0, 1], y: [8, 0] },
      { r: 'row2', at: [2.48, 2.68], y: [0, 92] },
      { r: 'thread', at: [2.56, 2.76], o: [0, 1], y: [-6, 0] },
      { r: 'learnBtn', at: [3.28, 3.36], o: [1, 0] },
      { r: 'learned', at: [3.32, 3.48], o: [0, 1], s: [0.8, 1] },
      { r: 'cvOrbi', at: [3.38, 3.5], o: [0, 1] },
    ],
    glows: [
      { r: 'navGlow2', at: 1.05, dur: 0.4 },
      { r: 'row1Glow', at: 2.1, dur: 0.45 },
      { r: 'learnGlow', at: 2.95, dur: 0.35 },
    ],
    orbis: [{ prefix: 'cv', pop: 3.42, laugh: 3.55 }],
    cursor: {
      at: [0, 1.0, 1.3, 1.42, 1.7, 1.82, 2.32, 2.46, 3.1, 3.24, 3.7, 99],
      x: [300, 300, navPoint(2)[0], navPoint(2)[0], SUB_CONV[0], SUB_CONV[0], ROW1[0], ROW1[0], LEARN[0], LEARN[0], 600, 600],
      y: [250, 250, navPoint(2)[1], navPoint(2)[1], SUB_CONV[1], SUB_CONV[1], ROW1[1], ROW1[1], LEARN[1], LEARN[1], 330, 330],
      clicks: [1.36, 1.76, 2.4, 3.18],
    },
    camera: {
      at: [0, 2.32, 2.6, 3.95, 4.25, 99],
      x: [320, 320, 384, 384, 320, 320],
      y: [176, 176, 215, 215, 176, 176],
      s: [1, 1, 1.25, 1.25, 1, 1],
    },
    still: 3.9,
  });
